/**
 * Адаптация смеси упражнения к состоянию пользователя.
 *
 * Смесь по умолчанию задана конфигурацией, но она меняется, если пользователь
 * устал, застрял или выполняет упражнения слишком легко (ТЗ FR-EN-05).
 */

import type { LearningConfig, MaterialMix } from './config/priority.v1.js';
import { clamp01 } from './normalize.js';

/** Наблюдаемое состояние пользователя в текущей сессии. */
export interface LearnerState {
  /** Точность последней сессии, 0..1. */
  readonly accuracy: number;
  /** Признак усталости: скорость и задержки ухудшились к концу сессии. */
  readonly fatigue: number;
  /** Доля единиц, где успех так и не достигнут. */
  readonly stuckRatio: number;
  /** Число сессий подряд с точностью выше верхней границы диапазона. */
  readonly effortlessSessions: number;
}

/** Как именно изменена смесь и почему. */
export interface MixDecision {
  readonly mix: MaterialMix;
  /** Причины отклонения от базовой смеси. Пустой массив — смесь базовая. */
  readonly reasons: readonly string[];
  /** Рекомендуемое изменение сложности: -1 упростить, 0 оставить, +1 усложнить. */
  readonly difficultyShift: -1 | 0 | 1;
}

/** Порог усталости, при котором нагрузка снижается. */
const FATIGUE_THRESHOLD = 0.5;

/** Доля «застрявших» единиц, при которой нужен передых от сложного. */
const STUCK_THRESHOLD = 0.35;

/** Сколько лёгких сессий подряд означает, что пора усложнять. */
const EFFORTLESS_SESSIONS_TO_ESCALATE = 2;

/** Нормирует смесь так, чтобы сумма долей была равна 1. */
export function normalizeMix(mix: MaterialMix): MaterialMix {
  const total = mix.weak + mix.due + mix.fresh + mix.maintenance + mix.transfer;
  if (total <= 0) return mix;
  return {
    weak: mix.weak / total,
    due: mix.due / total,
    fresh: mix.fresh / total,
    maintenance: mix.maintenance / total,
    transfer: mix.transfer / total,
  };
}

/**
 * Подбирает смесь материала под состояние пользователя.
 *
 * Правило приоритета при конфликте: сначала точность, затем скорость
 * (ТЗ §7.7). Поэтому падение точности перевешивает признак «слишком легко».
 */
export function adaptMix(state: LearnerState, config: LearningConfig): MixDecision {
  const [minAccuracy, maxAccuracy] = config.targetAccuracyRange;
  const base = config.baseMix;
  const reasons: string[] = [];

  let mix: MaterialMix = { ...base };
  let difficultyShift: -1 | 0 | 1 = 0;

  if (state.accuracy < minAccuracy) {
    // Точность просела: меньше нового, больше поддержания автоматизма.
    mix = { ...mix, fresh: mix.fresh * 0.4, maintenance: mix.maintenance + mix.fresh * 0.6 };
    difficultyShift = -1;
    reasons.push('точность ниже учебного диапазона');
  }

  if (state.fatigue > FATIGUE_THRESHOLD) {
    // Усталость: слабый материал закрепляет ошибку, а не исправляет её.
    const moved = mix.weak * 0.3;
    mix = { ...mix, weak: mix.weak - moved, maintenance: mix.maintenance + moved };
    if (difficultyShift === 0) difficultyShift = -1;
    reasons.push('признаки усталости');
  }

  if (state.stuckRatio > STUCK_THRESHOLD) {
    // Застревание: нужен освоенный материал, чтобы вернуть ритм.
    const moved = mix.weak * 0.25;
    mix = { ...mix, weak: mix.weak - moved, maintenance: mix.maintenance + moved };
    reasons.push('высокая доля неосвоенных единиц');
  }

  const effortless =
    state.accuracy > maxAccuracy && state.effortlessSessions >= EFFORTLESS_SESSIONS_TO_ESCALATE;
  if (effortless && difficultyShift >= 0) {
    // Слишком легко: больше нового и контрольного материала.
    const moved = mix.maintenance * 0.5;
    mix = {
      ...mix,
      maintenance: mix.maintenance - moved,
      fresh: mix.fresh + moved * 0.6,
      transfer: mix.transfer + moved * 0.4,
    };
    difficultyShift = 1;
    reasons.push('материал даётся слишком легко');
  }

  return { mix: normalizeMix(mix), reasons, difficultyShift };
}

/**
 * Оценка усталости по динамике сессии, 0..1.
 *
 * Сравнивается вторая половина сессии с первой: рост задержек и падение
 * точности к концу — основной наблюдаемый признак (ТЗ §7.7).
 */
export function estimateFatigue(
  firstHalfLatencyMs: number,
  secondHalfLatencyMs: number,
  firstHalfAccuracy: number,
  secondHalfAccuracy: number,
): number {
  if (firstHalfLatencyMs <= 0) return 0;

  const slowdown = clamp01((secondHalfLatencyMs - firstHalfLatencyMs) / firstHalfLatencyMs);
  const accuracyDrop = clamp01(firstHalfAccuracy - secondHalfAccuracy);

  return clamp01(slowdown * 0.6 + accuracyDrop * 2 * 0.4);
}
